/**
 * Image component module. For displaying logos, thumbnails, and other non-icon images.
 * @module shared/components/Image
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import Popover from './Popover';
import {layoutAttribute, fill, fillMaxHeight, fillMaxWidth} from './cssUtils';

const _Image = styled.img`
  margin: ${props => props.margin};
  max-height: ${props => props.maxHeight};
  ${props => props.maxWidth && fillMaxWidth(props.maxWidth === 'fill' ? null : props.maxWidth)};
  ${props => props.minHeight ? `min-height: ${props.minHeight};` : ''}
  ${props => props.width && fill(props.width === 'fill' ? null : props.width)};
  ${props => props.maxHeight && fillMaxHeight(props.maxHeight === 'fill' ? null : props.maxHeight)};
  ${props => props.padding ? `padding:${props.padding};` : ''};
  object-fit: contain;
  ${layoutAttribute.css};

  &:hover {
    ${props => props.onClick ? 'cursor: pointer' : ''};
  }

  &:hover[disabled] {
    cursor: not-allowed;
  }
`;

/**
 * Image component
 * @name Image
 */
const Image = (props) => {
  const {
    description,
    disabled,
    action,
    ...others
  } = props;

  return <Popover disabled={disabled} content={description}>
    <_Image
      {...others}
      description={description}
      disabled={disabled}
      onClick={action && (e => {
        if (disabled) {
          return;
        }

        e.stopPropagation();
        e.preventDefault();
        action(e);
      })}
    />
  </Popover>;
};

export default Image;

Image.propTypes = {
  /**
   * Action is executed on click of the image, unless if it's disabled
   */
  action: PropTypes.func,
  /**
   * Displays as a Popover on hover of the image
   */
  description: PropTypes.string,
  /**
   * Defaults to false. If disabled, no visual change to the image
   */
  disabled: PropTypes.bool,
  /**
   * The image file (png/svg/url/predefined item) to display
   */
  src: PropTypes.string.isRequired,
  /**
   * The margins for display
   */
  margin: PropTypes.string,
  /**
   * The max height for display
   */
  maxHeight: PropTypes.string.isRequired,
  /**
   * The max width for display
   */
  maxWidth: PropTypes.string.isRequired,
  /**
   * The min height for display
   */
  minHeight: PropTypes.string,
  /**
   * The min width for display
   */
  minWidth: PropTypes.string,
  /**
   * The width for display
   */
  width: PropTypes.string,
  /**
   * The padding for display
   */
  padding: PropTypes.string,
  layout: layoutAttribute.propTypes,
};

Image.defaultProps = {
  margin: '5px',
};
